// Forked from api/msw/api/caseDocuments/mockData.ts at `cases-pre-sr-fork` (Service Requests start as a copy of Cases).
// Comments below were written for Cases and still describe this file until the two diverge.
import { makeDocumentMockData } from '../../modules/documentMockDataFactory'
import type { IServiceRequestDocument } from '~/types/document/models'

/**
 * Seeded, unlike every other document collection, because two of these three
 * rows arrive from the email pipeline rather than from an upload -- and one of
 * them is the inline image that must be FILTERED OUT of any document list.
 *
 * ⚠ `uploadedByTeamMember` is never null: an extracted attachment is attributed
 * to Service Request Bot (XXTM0000), the identity Cynque writes as.
 */
export async function getStartingServiceRequestDocumentListData(): Promise<
  IServiceRequestDocument[]
> {
  return [
    // Uploaded through the UI by a person: no serviceRequestEmailId, no contentId.
    {
      id: 'XXSRD0001',
      hrid: 'SRD0001',
      serviceRequest: {
        id: 'XXSR0001',
        label: 'POTS line at Boston dropping calls',
      },
      descr: 'Signed site access form',
      documentType: { id: 8, label: 'LOA' },
      filename: 'boston-site-access.pdf',
      filetype: 'application/pdf',
      filesize: 184320,
      fileChecksum: 'd2a4f1c09b7e5a3c6f80b1d47e2c9a58',
      fileChecksumAlgorithm: 'md5',
      uploadedByTeamMember: { id: 'XXTM0006', label: 'Service Technician' },
      fileUrlPath: '/files/XXSRD0001',
      serviceRequestEmailId: null,
      contentId: null,
      createdAt: '2022-03-01T13:52:00Z',
      version: '1',
      canBeDeleted: true,
    },
    // A real attachment on the customer's inbound message.
    {
      id: 'XXSRD0002',
      hrid: 'SRD0002',
      serviceRequest: {
        id: 'XXSR0001',
        label: 'POTS line at Boston dropping calls',
      },
      documentType: { id: 6, label: 'Invoice' },
      filename: 'northwind-invoice-44812.pdf',
      filetype: 'application/pdf',
      filesize: 96114,
      fileChecksum: '7b1e0c46a9d35f28c4e7b0912da6e3f1',
      fileChecksumAlgorithm: 'md5',
      uploadedByTeamMember: { id: 'XXTM0000', label: 'Service Request Bot' },
      fileUrlPath: '/files/XXSRD0002',
      serviceRequestEmailId: 'XXSRE0001',
      contentId: null,
      createdAt: '2022-03-01T14:02:47Z',
      version: '1',
      canBeDeleted: false,
    },
    // ⚠ contentId SET -- the sender's signature logo, which the HTML body
    // references as `cid:`. Postmark delivers these in Attachments[]
    // indistinguishable from real files, so a document list that does not filter
    // on contentId fills up with them. Kept rather than dropped so the body can
    // still render its images.
    {
      id: 'XXSRD0003',
      hrid: 'SRD0003',
      serviceRequest: {
        id: 'XXSR0001',
        label: 'POTS line at Boston dropping calls',
      },
      filename: 'signature-logo.png',
      filetype: 'image/png',
      filesize: 4182,
      fileChecksum: 'a03f7cc1b58e42d96071fe3b8c5d04a7',
      fileChecksumAlgorithm: 'md5',
      uploadedByTeamMember: { id: 'XXTM0000', label: 'Service Request Bot' },
      fileUrlPath: '/files/XXSRD0003',
      serviceRequestEmailId: 'XXSRE0001',
      contentId: 'logo@northwindlogistics.com',
      createdAt: '2022-03-01T14:02:47Z',
      version: '1',
      canBeDeleted: false,
    },
  ]
}

export const {
  getDocumentListData: getServiceRequestDocumentListData,
  getDocumentDataById: getServiceRequestDocumentDataById,
  getDocumentListDataByParentId:
    getServiceRequestDocumentListDataByServiceRequestId,
} = makeDocumentMockData<IServiceRequestDocument>({
  entityName: 'serviceRequest',
  // Lowercase: the key is `${prefix}documents` and must equal the
  // 'servicerequestdocuments' the reply and merge handlers write.
  storageKeyPrefix: 'servicerequest',
  getStartingListData: getStartingServiceRequestDocumentListData,
})
